import { Button } from "@drone-js/core";
import { DynamicMenu } from "apps/commons/components/DynamicMenu";
import { HStack } from "apps/commons/components/Stack";
import { useDisclosure } from "apps/commons/hooks/useDisclosure";
import { isLocationFormFilled } from "apps/oneOnboarding/location/isLocationFormFilled";
import { useStaticDataAtomValue } from "apps/oneOnboarding/staticDataAtom";
import type { PropertyData } from "libs/codegen/property-onboarding";
import type { FunctionComponent } from "react";
import { type FieldPath, useWatch } from "react-hook-form";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";
import { DeletePropertyModal } from "./DeletePropertyModal";
import { LocationFormModal } from "./LocationFormModal";
import { PropertyImage } from "./PropertyImage";
import { PropertyName } from "./PropertyName";

interface Props {
    id: number;
    onboardingId: string;
    name: string;
    location?: string;
    image?: string;
    fieldPrefix: string;
    onDelete: () => void;
}

type ValidPath = FieldPath<BulkPropertyFormData>;

export const PropertyDetail: FunctionComponent<Props> = (props) => {
    const { id, onboardingId, name, location, image, fieldPrefix, onDelete } = props;
    const {
        cmsData: { bulkLocation },
    } = useStaticDataAtomValue();

    const { open: moreActionsOpen, onToggle: onMoreActionsToggle } = useDisclosure();
    const { open: deletePropertyOpen, onToggle: onDeletePropertyToggle } = useDisclosure();
    const { open: locationFormOpen, onToggle: onLocationFormToggle } = useDisclosure();

    const propertyData = useWatch<BulkPropertyFormData>({
        name: fieldPrefix as ValidPath,
    }) as PropertyData;
    const isChecked = isLocationFormFilled(propertyData.state?.id, propertyData.city?.id, propertyData.streetAddress);

    const handleDeletePropertyModal = () => {
        onMoreActionsToggle();
        setTimeout(() => {
            onDeletePropertyToggle();
        }, 100);
    };

    return (
        <HStack justifyContent="space-between" alignItems="center">
            <HStack gap="12" alignItems="center">
                <PropertyImage checked={isChecked} image={image} name={name} />
                <PropertyName name={name} location={location} />
            </HStack>
            <HStack gap="8">
                <Button
                    variant="label"
                    size="m"
                    label={isChecked ? bulkLocation.edit : bulkLocation.add}
                    hierarchy={isChecked ? "secondary" : "primary"}
                    onClick={onLocationFormToggle}
                    data-element-name={isChecked ? "edit-location" : "add-location"}
                    data-testid={isChecked ? `edit-location-${id}` : `add-location-${id}`}
                />
                <DynamicMenu
                    title="More actions"
                    items={[
                        {
                            id: `menu-${id}-delete`,
                            title: bulkLocation.delete,
                            onClick: handleDeletePropertyModal,
                            dataElementName: "remove-listing",
                            dataTestId: "remove-listing",
                        },
                    ]}
                    open={moreActionsOpen}
                    onToggle={onMoreActionsToggle}
                    dataElementName="more-actions"
                    dataTestId="more-actions"
                    dataPropertyId={id}
                    dataOnboardingId={onboardingId}
                />
            </HStack>
            <DeletePropertyModal
                id={id}
                open={deletePropertyOpen}
                onClose={onDeletePropertyToggle}
                onDelete={onDelete}
            />
            <LocationFormModal
                id={id}
                open={locationFormOpen}
                onClose={onLocationFormToggle}
                fieldPrefix={fieldPrefix}
            />
        </HStack>
    );
};
